import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { errorHandler, ErrorCategory, ErrorSeverity } from '@/utils/errorHandling';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      errorId: `boundary_${Date.now()}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Capture error with full context
    const appError = errorHandler.captureError(error, {
      severity: ErrorSeverity.CRITICAL,
      category: ErrorCategory.SYSTEM,
      context: {
        component: 'ErrorBoundary',
        errorBoundary: true,
        componentStack: errorInfo.componentStack,
        errorBoundaryProps: this.props,
        additionalData: {
          errorInfo,
          timestamp: new Date().toISOString()
        }
      }
    });

    // Log the error
    logger.error('Error caught by boundary', { 
      component: 'ErrorBoundary',
      action: 'componentDidCatch' 
    }, appError);

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Store error ID for user reference
    this.setState({ errorId: appError.id });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorId: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorId: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center">
              <AlertCircle className="mx-auto h-16 w-16 text-destructive" />
              <h1 className="mt-4 text-2xl font-bold text-foreground">
                Oops! Something went wrong
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We've encountered an unexpected error. Our team has been automatically notified.
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Error ID:</strong> {this.state.errorId}
                <br />
                <span className="text-xs text-muted-foreground">
                  Please include this ID when contacting support.
                </span>
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Button 
                onClick={this.handleRetry} 
                className="w-full"
                variant="default"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleGoHome} 
                variant="outline"
                className="w-full"
              >
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 p-4 bg-muted rounded-md">
                <summary className="cursor-pointer text-sm font-medium">
                  Developer Details
                </summary>
                <pre className="mt-2 text-xs overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}